import {Overlay} from "@angular/cdk/overlay"
import {SlicePipe} from "@angular/common"
import {AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, OnDestroy, output, signal, viewChild} from "@angular/core"
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatDividerModule} from "@angular/material/divider"
import {MatMenuModule} from "@angular/material/menu"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {ActivatedRoute, Router} from "@angular/router"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {RenameDialogComponent} from "@app/common/components/dialogs/rename-dialog/rename-dialog.component"
import {HintComponent, HintTypes} from "@app/common/components/hint/hint.component"
import {JobTaskProgressComponent} from "@common/components/job-task-progress/job-task-progress.component"
import {DialogSize} from "@app/common/models/dialogs"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {TextureEditorDataObjectFragment, TextureSetRevisionDataFragment} from "@app/textures/service/textures-api.generated"
import {OperatorInspectorComponent} from "@app/textures/texture-editor/operators-panel/operator-inspector/operator-inspector.component"
import {byOperatorType, isKnownOperatorType} from "@app/textures/texture-editor/operators-panel/operator-desc"
import {OperatorsPanelComponent} from "@app/textures/texture-editor/operators-panel/operators-panel.component"
import {TextureSelectorComponent} from "@app/textures/texture-editor/texture-selector/texture-selector.component"
import {TilingCanvasComponent} from "@app/textures/texture-editor/tiling-canvas/tiling-canvas.component"
import {TilingControlsComponent} from "@app/textures/texture-editor/tiling-controls/tiling-controls.component"
import {TilingInfoPanelComponent} from "@app/textures/texture-editor/tiling-info-panel/tiling-info-panel.component"
import {getRevisionTitle} from "@app/textures/utils/texture-set-revision-utils"
import {Size2, Size2Like} from "@cm/math"
import {IsDefined, IsUnique} from "@cm/utils/filter"
import {CanvasPhysicalInfo} from "@common/components/canvas"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {AuthService} from "@common/services/auth/auth.service"
import {HotkeyLayerId, Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {ImageDataType, JobState, TextureSetRevisionMapAssignmentInput, TextureType} from "@generated"
import {TexturesApiService} from "app/textures/service/textures-api.service"
import {AdjustSizeDialogComponent, AdjustSizeDialogResult} from "@app/textures/texture-editor/adjust-size-dialog/adjust-size-dialog.component"
import {OperatorStack} from "@app/textures/texture-editor/operator-stack/operator-stack"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {TextureEditProcessing} from "@app/textures/texture-editor/texture-edit-processing"
import {
    getBakedMapDataObjectIds,
    getSourceMapDataObjectIds,
    SourceData,
    TextureEditorCallback,
    TextureEditorData,
    TextureSetRevisionSpecific,
    TextureTypeSpecific,
} from "@app/textures/texture-editor/texture-editor-callback"
import {getSimpleJobState, SimpleJobState} from "app/textures/utils/simple-job-state"
import {debounceTime, filter, first, firstValueFrom, interval} from "rxjs"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {AnyImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {MatTooltip} from "@angular/material/tooltip"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {CM_LOGGER_TEXTURES} from "@app/common/services/logging"
import {deepEqual} from "@cm/utils"

const MIN_SUPPORTED_SCHEMA_VERSION = 3

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-editor",
    templateUrl: "./texture-editor.component.html",
    styleUrls: ["./texture-editor.component.scss"],
    imports: [
        HintComponent,
        JobTaskProgressComponent,
        MatMenuModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        OperatorsPanelComponent,
        OperatorInspectorComponent,
        RoutedDialogComponent,
        TextureSelectorComponent,
        TilingCanvasComponent,
        TilingControlsComponent,
        TilingInfoPanelComponent,
        ButtonComponent,
        SlicePipe,
        MatTooltip,
    ],
})
export class TextureEditorComponent implements AfterViewInit, OnDestroy, TextureEditorCallback {
    private readonly logger = inject(CM_LOGGER_TEXTURES)
    readonly $overlayDialog = viewChild.required<RoutedDialogComponent>("overlayDialog")
    readonly $tilingCanvas = viewChild.required<TilingCanvasComponent>("tilingCanvasRef")

    readonly sourceDataChanged = output<SourceData | null>()

    get data(): TextureEditorData | undefined {
        return this.$data()
    }

    readonly $physicalInfo = signal<CanvasPhysicalInfo>({
        pixelsPerCm: 0,
        originXCm: 0,
        originYCm: 0,
    })
    firstTimeLoad = true

    DialogSize = DialogSize
    HintTypes = HintTypes

    readonly $gridActive = signal(false)
    readonly $gridOutlineActive = signal(false)

    readonly $isDebugEnabled = signal(TextureEditorSettings.EnableDebug)

    private readonly hotkeyLayerId: HotkeyLayerId

    protected readonly permission = inject(PermissionsService)
    private readonly destroyRef = inject(DestroyRef)
    readonly overlay = inject(Overlay)
    readonly router = inject(Router)
    readonly route = inject(ActivatedRoute)
    private readonly dialog = inject(MatDialog)
    private readonly notification = inject(NotificationsService)
    private readonly textureApi = inject(TexturesApiService)
    private readonly refresh = inject(RefreshService)
    private readonly authService = inject(AuthService)
    private readonly hotkeys = inject(Hotkeys)
    private readonly injector = inject(Injector)
    protected $can = this.permission.$to

    constructor() {
        TextureEditorSettings.UseVT = this.$can().read.imageTiles
        // register this.hotkeys
        this.hotkeyLayerId = this.hotkeys.createLayer() // create a layer since this is a modal dialog
        this.hotkeys
            .addShortcut(["control.shift.d"])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.authService.isStaff()) {
                    this.$isDebugEnabled.update((value) => !value)
                    this.logger.info(this.$isDebugEnabled() ? "DEBUG MODE ENABLED" : "DEBUG MODE DISABLED")
                }
            })
        this.hotkeys
            .addShortcut(["d"])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.previousRevision())
        this.hotkeys
            .addShortcut(["f"])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.nextRevision())
        this.hotkeys
            .addShortcut("g")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (!this.$gridActive()) this.toggleGrid()
                this.toggleGridOutline()
            })
        this.hotkeys
            .addShortcut("t")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toggleGrid()
            })
        this.hotkeys
            .addShortcut("b")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toggleBrightnessBoost()
            })
        this.hotkeys
            .addShortcut("Escape")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.$overlayDialog().closeIfClean())
        this.hotkeys
            .addShortcut(["control.s", "meta.s"])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.$isOperatorStackValid() && this.$isEdited()) {
                    void this.save()
                }
            })

        this._textureEditProcessing = new TextureEditProcessing(this.injector)
    }

    ngAfterViewInit(): void {
        const operatorStack = new OperatorStack(this, this.injector, this.$tilingCanvas())
        this.$_operatorStack.set(operatorStack)
        operatorStack.resultImageChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((image) => this.onResultImageChanged(image))
        this.$tilingCanvas()
            .$canvas()
            .canvasBoundsChanged$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((bounds) => this.onCanvasBoundsChange(bounds))
        this.route.url.subscribe(() => this.changeTexture())
        this.route.queryParamMap.subscribe(() => this.changeTexture())
        interval(3000)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => !document.hidden),
            )
            .subscribe(() => this.checkForProcessingEdits())
    }

    // passed as TilingCanvasComponent's onAboutToBeDestroyed input, awaited before it disposes the WebGL2 context
    protected readonly disposeOperatorStack = async (): Promise<void> => {
        await this.$_operatorStack()?.dispose()
        this.$_operatorStack.set(undefined)
        this.hotkeys.removeLayer(this.hotkeyLayerId)
    }

    ngOnDestroy(): void {}

    get isDebugEnabled(): boolean {
        return this.$isDebugEnabled()
    }

    get imageIsUpToDate(): boolean {
        return !this._tilingCanvasShowsResultDataObject || this.resultDataObjectUpToDate
    }

    protected async onFrameDrawn(): Promise<void> {
        if (this._handledFirstFrame) {
            return
        }
        this._handledFirstFrame = true
        try {
            if (!this._cacheBackfillAttemptedForRevision) {
                this._cacheBackfillAttemptedForRevision = true
                await this.maybePersistCacheBackfill()
            }
        } finally {
            this.$_loadingComplete.set(true)
        }
    }

    protected onCanvasLoadError(error: Error): void {
        this.$_canvasLoadError.set(error)
    }

    private onResultImageChanged(image: AnyImageOpHal | null) {
        this.$_canvasLoadError.set(null)
        this.$resultResolution.set(image ? new Size2(image.descriptor.width, image.descriptor.height) : new Size2(0, 0))
        this.$tilingCanvas().setResultImage(image)
        this.$tilingCanvas().setMessage(
            this.imageIsUpToDate ? undefined : "Edits have been made and HQ processing has not completed yet so this image might be outdated.",
        )
    }

    private async checkForProcessingEdits() {
        const data = this.$data()
        if (!data) {
            return
        }
        this.textureApi
            .queryTextureSetRevisionProcessingJobState(data.textureSetRevisionSpecific.textureSetRevision.id)
            .then((result) => result.textureSetRevision.editsProcessingJob)
            .then((editsProcessingJob) => {
                if (editsProcessingJob) {
                    const justCompleted =
                        data.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob()?.state !== JobState.Complete &&
                        editsProcessingJob.state === JobState.Complete
                    data.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob.set(editsProcessingJob)
                    if (justCompleted && !this.$isEdited()) {
                        this.refresh.item({id: data.textureSetRevisionSpecific.textureSetRevision.id, __typename: "TextureSetRevision"})
                        this.reload()
                    }
                } else {
                    data.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob.set(undefined)
                }
            })
            .catch((_error) => {
                data.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob.set(undefined)
            })
    }

    private reload() {
        this._lastRequestedTextureInfo = {textureSetRevisionId: "", textureType: undefined}
        void this.changeTexture()
    }

    private onCanvasBoundsChange(bounds: Size2Like): void {
        const hasBounds = bounds.width > 0 && bounds.height > 0
        if (this.firstTimeLoad && hasBounds) {
            this.$tilingCanvas().$canvas().zoomToFitCanvasBounds(0.95)
            this.firstTimeLoad = false
        }
    }

    protected zoomToPhysicalPixels() {
        this.$tilingCanvas().$canvas().zoomToPhysicalPixels()
    }

    get enableDebugFeatures(): boolean {
        return this.authService.isSuperuser()
    }

    readonly $isBusy = computed(() => this.$_busyCounter() > 0 || (this.$_operatorStack()?.isBusy ?? false))
    readonly $showSpinner = toSignal(toObservable(this.$isBusy).pipe(debounceTime(200)), {initialValue: false})

    readonly $isInitialFrameDrawn = computed(() => this.$_loadingComplete())

    readonly $resultImageError = computed(() => this.$_operatorStack()?.resultImageError ?? this.$_canvasLoadError())
    // Only a fatal "revision failed to load" before any frame has ever been drawn. Errors during
    // normal editing after a successful initial load keep today's toast-only behavior.
    readonly $initialLoadError = computed(() => (!this.$isInitialFrameDrawn() ? this.$resultImageError() : null))

    readonly $canEditDisplacementMap = computed(() => {
        return (
            !this.$data()?.textureSetRevisionSpecific.autoGenerateDisplacementMap || this.$data()?.textureTypeSpecific?.textureType !== TextureType.Displacement
        )
    })

    readonly $availableTextureTypesForOperators = computed(() => {
        const data = this.$data()
        if (!data) {
            return []
        } else if (data.textureSetRevisionSpecific.autoGenerateDisplacementMap) {
            return data.textureSetRevisionSpecific.availableTextureTypes.filter((textureType) => textureType !== TextureType.Displacement)
        } else {
            return data.textureSetRevisionSpecific.availableTextureTypes
        }
    })

    readonly $isLegacyUnsupportedEdit = computed(() => this.$data()?.textureSetRevisionSpecific.isLegacyUnsupportedEdit ?? false)

    readonly $isDataValid = computed(
        () => !!this.$data() && !this.$initialLoadError() && this.$hasValidResolutions() && this.$hasValidRatio() && !this.$isLegacyUnsupportedEdit(),
    )

    readonly $canEdit = computed(() => this.$isDataValid() && !this.$isInUse() && this.$canEditDisplacementMap())

    private async changeTexture(): Promise<void> {
        const textureSetRevisionId = this.route.snapshot.paramMap.get("textureSetRevisionId") as string
        const requestedTextureType = this.route.snapshot.queryParams.textureType as TextureType | undefined

        const textureSetRevisionChanged = this._lastRequestedTextureInfo.textureSetRevisionId !== textureSetRevisionId
        const textureTypeChanged = this._lastRequestedTextureInfo.textureType !== requestedTextureType
        if (!textureSetRevisionChanged && !textureTypeChanged) {
            // we have already loaded the correct texture
            return
        }
        this._lastRequestedTextureInfo = {textureSetRevisionId, textureType: requestedTextureType}
        this.$_isEdited.set(false)
        this.$_loadingComplete.set(false)
        this.$_operatorStack()?.clearResultImageError()
        this.$_canvasLoadError.set(null)

        this._handledFirstFrame = false
        if (textureSetRevisionChanged) {
            this._cacheBackfillAttemptedForRevision = false
        }

        this.busyInc()
        try {
            let textureSetRevisionSpecific: TextureSetRevisionSpecific | undefined
            if (textureSetRevisionChanged) {
                const textureSetRevisionData = await this.textureApi.queryTextureSetRevision(textureSetRevisionId)
                const textureSetData = await this.textureApi.queryTextureSet(textureSetRevisionData.textureSet.id)
                const isLegacyUnsupportedEdit = editsJsonHasUnsupportedOperator(textureSetRevisionData.editsJson)
                if (!isLegacyUnsupportedEdit && textureSetRevisionData.editsJson) {
                    if (textureSetRevisionData.editsJson.type !== "texture-edits" || textureSetRevisionData.editsJson.schema < MIN_SUPPORTED_SCHEMA_VERSION) {
                        this.notification.showError("Unsupported texture edits schema. Ignoring edits.")
                        textureSetRevisionData.editsJson = undefined
                    }
                }
                const editsJson = isLegacyUnsupportedEdit ? undefined : (textureSetRevisionData.editsJson as TextureEditNodes.TextureEdits | undefined)
                const sortedTextureSetRevisionDescriptors = [...textureSetData.textureSetRevisions]
                    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
                    .map((revision, index) => ({...revision, version: index + 1}))
                    .reverse()
                const textureSetRevisionVersion = sortedTextureSetRevisionDescriptors.find((revision) => revision.id === textureSetRevisionId)!.version
                if (textureSetRevisionVersion == null) {
                    throw Error("INTERNAL ERROR: Texture set revision version not found.")
                }
                const checkHasValidResolutions = (data: TextureSetRevisionDataFragment) => {
                    // make sure all maps have the same resolution
                    const mapAssignmentSizes = data.mapAssignments.map((assignment) => ({
                        width: assignment.dataObject.width ?? 0,
                        height: assignment.dataObject.height ?? 0,
                    }))
                    if (mapAssignmentSizes.length === 0) {
                        return false
                    }
                    const firstMapResolution = mapAssignmentSizes[0]
                    if (firstMapResolution.width === 0 || firstMapResolution.height === 0) {
                        return false
                    }
                    const hasConsistentMapResolutions = mapAssignmentSizes.every(
                        (size) => size.width === firstMapResolution.width && size.height === firstMapResolution.height,
                    )
                    return hasConsistentMapResolutions
                }
                const editsProcessingJob = await this.textureApi
                    .queryTextureSetRevisionProcessingJobState(textureSetRevisionData.id)
                    .then((result) => result.textureSetRevision.editsProcessingJob)
                const textureSetRevision = {...textureSetRevisionData, version: textureSetRevisionVersion, $editsProcessingJob: signal(editsProcessingJob)}
                const sourceMapIds = getSourceMapDataObjectIds(textureSetRevision)
                const bakedMapIds = getBakedMapDataObjectIds(textureSetRevision)
                const availableTextureTypes = Array.from(sourceMapIds.keys()).filter((textureType) => bakedMapIds.has(textureType))
                const isInUse = textureSetRevision.materialRevisionReferences.length > 0
                const hasValidResolutions = checkHasValidResolutions(textureSetRevision)
                const hasValidRatio = this.checkHasValidRatio(textureSetRevision)
                const autoGenerateDisplacementMap = true
                if (autoGenerateDisplacementMap) {
                    // in case we auto-generate displacement maps we can always show the resulting map even if no source map was given
                    if (bakedMapIds.has(TextureType.Displacement) && !availableTextureTypes.includes(TextureType.Displacement)) {
                        availableTextureTypes.push(TextureType.Displacement)
                    }
                }
                textureSetRevisionSpecific = {
                    textureSetRevision,
                    textureEdits: editsJson,
                    availableTextureTypes,
                    textureSet: textureSetData,
                    sortedTextureSetRevisionDescriptors,
                    isInUse,
                    hasValidResolutions,
                    hasValidRatio,
                    autoGenerateDisplacementMap,
                    isLegacyUnsupportedEdit,
                }
            } else {
                textureSetRevisionSpecific = this.$data()?.textureSetRevisionSpecific
            }

            if (!textureSetRevisionSpecific) {
                return
            }

            // select texture type as requested or default to diffuse or first available
            const textureType =
                (requestedTextureType
                    ? textureSetRevisionSpecific.availableTextureTypes.find((textureType) => textureType === requestedTextureType)
                    : undefined) ??
                textureSetRevisionSpecific.availableTextureTypes.find((textureType) => textureType === TextureType.Diffuse) ??
                textureSetRevisionSpecific.availableTextureTypes[0]

            // select texture revision by source map if edits have been made or finally assigned data if no edits have been made
            const sourceDataObjectId = getSourceMapDataObjectIds(textureSetRevisionSpecific.textureSetRevision).get(textureType)
            const resultDataObjectId = getBakedMapDataObjectIds(textureSetRevisionSpecific.textureSetRevision).get(textureType)
            // if (!sourceDataObjectId) {
            //     this.notification.showError("INTERNAL ERROR: Source data-object not found for texture type.")
            // }
            if (!resultDataObjectId) {
                this.notification.showError("INTERNAL ERROR: Result data-object not found for texture type.")
            }
            const sourceDataObject = sourceDataObjectId ? await this.textureApi.queryDataObject(sourceDataObjectId) : undefined
            const resultDataObject = resultDataObjectId ? await this.textureApi.queryDataObject(resultDataObjectId) : undefined
            let textureTypeSpecific: TextureTypeSpecific | undefined = undefined
            if (!resultDataObject) {
                this.notification.showError("INTERNAL ERROR: Result data-object is missing.")
            } else if (!resultDataObject.width || !resultDataObject.height) {
                this.notification.showError("INTERNAL ERROR: Result data-object is missing width/height.")
            } else {
                const pxPerCm = new Size2(
                    resultDataObject.width / textureSetRevisionSpecific.textureSetRevision.width,
                    resultDataObject.height / textureSetRevisionSpecific.textureSetRevision.height,
                )
                textureTypeSpecific = {
                    textureType: textureType,
                    sourceDataObject: sourceDataObject ?? resultDataObject, // this is needed for auto-generated displacement maps
                    resultDataObject,
                    pxPerCm,
                }
            }

            await this.setData({
                textureSetRevisionSpecific,
                textureTypeSpecific,
            })
        } catch (error) {
            await this.setData(undefined)
            this.notification.showError("Error loading texture set revision: " + error)
        } finally {
            this.busyDec()
        }
    }

    private checkHasValidRatio(data: TextureSetRevisionDataFragment) {
        // make sure all maps have the same resolution
        const firstMapResolution = data.mapAssignments
            .map((assignment) => ({
                width: assignment.dataObject.width ?? 0,
                height: assignment.dataObject.height ?? 0,
            }))
            .find(() => true)
        if (!firstMapResolution || firstMapResolution.width === 0 || firstMapResolution.height === 0) {
            return false
        }
        const eps = 1e-4
        const hasSquarePixels = Math.abs(firstMapResolution.width / data.width - firstMapResolution.height / data.height) < eps
        return hasSquarePixels
    }

    readonly $brightnessBoostActive = signal(false)

    readonly $hasOperators = computed(() => {
        const operatorStack = this.$_operatorStack()
        return !!operatorStack && !!operatorStack.operators.find((operator) => !operator.flags.has("is-internal"))
    })

    readonly $isEdited = computed(() => this.$_isEdited() || (this.$_operatorStack()?.isEdited ?? false))

    readonly $isOperatorStackValid = computed(() => this.$_operatorStack()?.isValid ?? true)

    readonly $operatorValidationMessages = computed(() =>
        (this.$_operatorStack()?.operators ?? []).filter((op) => !op.$valid()).map((op) => `${byOperatorType[op.type].label}: ${op.$validationMessage()}`),
    )

    readonly $isInUse = computed(() => this.$data()?.textureSetRevisionSpecific.isInUse ?? false)

    readonly $hasValidResolutions = computed(() => this.$data()?.textureSetRevisionSpecific.hasValidResolutions ?? true)

    readonly $hasValidRatio = computed(() => this.$data()?.textureSetRevisionSpecific.hasValidRatio ?? true)

    readonly $isProcessingEdits = computed(() => {
        const jobState = this.$data()?.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob()?.state
        return !!jobState && getSimpleJobState(jobState) !== SimpleJobState.Complete
    })

    readonly $processingJobId = computed(() => {
        if (!this.$isProcessingEdits()) return undefined
        return this.$data()?.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob()?.id
    })

    get resultDataObjectUpToDate(): boolean {
        const resultDataObject = this.$data()?.textureTypeSpecific?.resultDataObject
        return !!resultDataObject && !this.$isEdited() && !this.$isProcessingEdits()
    }

    private async setData(data: TextureEditorData | undefined) {
        const operatorStack = this.$_operatorStack()
        if (!operatorStack) {
            throw Error("Operator stack not initialized.")
        }

        const textureSetRevisionChanged = this.$data()?.textureSetRevisionSpecific !== data?.textureSetRevisionSpecific
        this.$data.set(data)

        // console.log(`Current revision id: ${this.$data()?.textureSetRevisionSpecific.textureSetRevision.id}`)
        // console.log(`Source map: ${this.$data()?.textureTypeSpecific.sourceDataObject.jpgDataObject?.downloadUrl ?? "none"}`)
        // console.log(`Result map: ${this.$data()?.textureTypeSpecific.resultDataObject.jpgDataObject?.downloadUrl ?? "none"}`)

        let sourceData: SourceData | null = null
        if (data) {
            let dataObject: TextureEditorDataObjectFragment | undefined = undefined
            let dataObjectIsResult: boolean
            // for auto-generated displacement maps, and for legacy edits that can no longer be reconstructed, we display the
            // baked map or a placeholder if the baked map is not available
            if (
                data.textureSetRevisionSpecific.isLegacyUnsupportedEdit ||
                (data.textureSetRevisionSpecific.autoGenerateDisplacementMap && data.textureTypeSpecific?.textureType === TextureType.Displacement)
            ) {
                dataObject = data.textureTypeSpecific?.resultDataObject
                dataObjectIsResult = true
            } else {
                dataObject = data.textureTypeSpecific?.sourceDataObject
                dataObjectIsResult = false
            }
            if (dataObject) {
                if (!dataObject.width) {
                    throw Error("Data object is missing width/height.")
                }
                sourceData = {
                    sourceImage: {
                        dataObjectId: dataObject.id,
                        isResult: dataObjectIsResult,
                        downloadFormat: TextureEditorSettings.UseVT ? "tiles" : "jpg",
                    },
                    textureEdits: textureSetRevisionChanged ? (data?.textureSetRevisionSpecific.textureEdits ?? null) : undefined,
                }
                this.$tilingCanvas().$isSrgb.set(dataObject.imageDataType === ImageDataType.Color)
                this._tilingCanvasShowsResultDataObject = dataObjectIsResult
                this.$physicalInfo.update((current) => ({...current, pixelsPerCm: data.textureTypeSpecific?.pxPerCm.width ?? 1}))
            }
        }
        this.sourceDataChanged.emit(sourceData)
        await this.checkForProcessingEdits()
    }

    private previousRevision(): void {
        const data = this.$data()
        if (!data) {
            throw Error("No selected texture revision info.")
        }
        const revisions = data.textureSetRevisionSpecific.textureSet.textureSetRevisions
        const currentIndex = revisions.findIndex((revision) => revision.id === data.textureSetRevisionSpecific.textureSetRevision.id)
        if (currentIndex < revisions.length - 1) {
            this.setTextureSetRevision(revisions[currentIndex + 1].id)
        }
    }

    private nextRevision(): void {
        const data = this.$data()
        if (!data) {
            throw Error("No selected texture revision info.")
        }
        const revisions = data.textureSetRevisionSpecific.textureSet.textureSetRevisions
        const currentIndex = revisions.findIndex((revision) => revision.id === data.textureSetRevisionSpecific.textureSetRevision.id)
        if (currentIndex > 0) {
            this.setTextureSetRevision(revisions[currentIndex - 1].id)
        }
    }

    // zoomTo(percentage: number): void {
    //     this.$tilingCanvas().$canvasBase().navigation.zoomTo(percentage)
    // }

    protected toggleGrid(): void {
        this.$gridActive.set(!this.$gridActive())
        if (!this.$gridActive()) {
            this.$gridOutlineActive.set(false)
        }
    }

    protected toggleGridOutline(): void {
        if (!this.$gridActive()) return
        this.$gridOutlineActive.set(!this.$gridOutlineActive())
    }

    protected toggleBrightnessBoost(): void {
        const newValue = !this.$brightnessBoostActive()
        this.$brightnessBoostActive.set(newValue)
        this.$tilingCanvas().displayGamma = newValue ? 1 / 2.2 : 1
    }

    protected async adjustSize() {
        const data = this.$data()
        if (!data) {
            throw Error("No texture set revision data set.")
        }
        if (!data.textureTypeSpecific) {
            throw Error("No texture type specific data set.")
        }
        if (!data.textureTypeSpecific.resultDataObject.width || !data.textureTypeSpecific.resultDataObject.height) {
            throw Error("Texture set revision has no width or height resolution set.")
        }
        const dialogRef: MatDialogRef<AdjustSizeDialogComponent, AdjustSizeDialogResult | undefined> = this.dialog.open(AdjustSizeDialogComponent, {
            width: "350px",
            data: {
                width: data.textureSetRevisionSpecific.textureSetRevision.width,
                height: data.textureSetRevisionSpecific.textureSetRevision.height,
                resolutionAspectRatio: data.textureTypeSpecific.resultDataObject.width / data.textureTypeSpecific.resultDataObject.height,
                displacement: data.textureSetRevisionSpecific.textureSetRevision.displacement,
                showDisplacementSetting: true,
                allowUndefinedDisplacement: true,
            },
        })
        const result = await firstValueFrom(dialogRef.afterClosed())
        if (!result) {
            return
        }
        this.notification.withUserFeedback(
            () =>
                this.textureApi.updateTextureSetRevision(data.textureSetRevisionSpecific.textureSet.id, {
                    id: data.textureSetRevisionSpecific.textureSetRevision.id,
                    width: result.width,
                    height: result.height,
                    displacement: result.displacement,
                }),
            {
                success: () => {
                    data.textureSetRevisionSpecific.textureSetRevision.width = result.width
                    data.textureSetRevisionSpecific.textureSetRevision.height = result.height
                    data.textureSetRevisionSpecific.textureSetRevision.displacement = result.displacement
                    data.textureSetRevisionSpecific.hasValidRatio = this.checkHasValidRatio(data.textureSetRevisionSpecific.textureSetRevision)
                    if (!data.textureTypeSpecific?.resultDataObject.width || !data.textureTypeSpecific?.resultDataObject.height) {
                        throw Error("Texture set revision has no width or height resolution set.")
                    }
                    data.textureTypeSpecific.pxPerCm = new Size2(
                        data.textureTypeSpecific.resultDataObject.width / result.width,
                        data.textureTypeSpecific.resultDataObject.height / result.height,
                    )
                    this.$data.set(this.$data())
                    return "Physical size adjusted. Note that the adjustment will not get automatically propagated to the materials which use the modified textures"
                },
                error: "Error adjusting physical size.",
            },
        )
    }

    protected async renameRevision() {
        const data = this.$data()
        if (!data) {
            throw Error("No texture set revision data set.")
        }
        const currentRevision = data.textureSetRevisionSpecific.textureSetRevision
        const dialogRef = this.dialog.open(RenameDialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                currentName: currentRevision.name,
                allowUndefined: true,
            },
        })
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (newName) => {
                if (typeof newName === "string") {
                    if (newName === "") {
                        newName = null
                    }
                    void this.notification.withUserFeedback(
                        () =>
                            this.textureApi.updateTextureSetRevision(data.textureSetRevisionSpecific.textureSet.id, {
                                id: currentRevision.id,
                                name: newName,
                            }),
                        {
                            success: () => {
                                currentRevision.name = newName
                                data.textureSetRevisionSpecific.sortedTextureSetRevisionDescriptors.find(
                                    (descriptor) => descriptor.id === currentRevision.id,
                                )!.name = newName
                                this.$data.set(this.$data())
                                this.refresh.item({id: data.textureSetRevisionSpecific.textureSet.id, __typename: "TextureSet"})
                                return "Revision renamed."
                            },
                            error: "Error renaming texture revision.",
                        },
                    )
                }
            })
    }

    protected async copyCurrentRevision(copyOperators: boolean) {
        const data = this.$data()
        if (!data) {
            throw Error("No texture set revision data set.")
        }
        if (!data.textureTypeSpecific) {
            throw Error("No texture type specific data set.")
        }
        this.busyInc()
        try {
            const operatorStack = this.$operatorStack()
            operatorStack.muteUpdates = true // prevent operator stack updates while copying
            try {
                const name = data.textureSetRevisionSpecific.textureSetRevision.name
                    ? data.textureSetRevisionSpecific.textureSetRevision.name + " (copy)"
                    : undefined
                const newTextureSetRevisionId = await this.textureApi
                    .createNewTextureSetRevision({
                        textureSetId: data.textureSetRevisionSpecific.textureSet.id,
                        name,
                        width: data.textureSetRevisionSpecific.textureSetRevision.width,
                        height: data.textureSetRevisionSpecific.textureSetRevision.height,
                        mapAssignments: data.textureSetRevisionSpecific.textureSetRevision.mapAssignments.map((assignment) => ({
                            textureType: assignment.textureType,
                            dataObjectId: assignment.dataObject.id,
                        })),
                    })
                    .then((result) => result.createTextureSetRevision.id)
                data.textureSetRevisionSpecific.textureSetRevision.id = newTextureSetRevisionId
                data.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob.set(undefined)
                if (!copyOperators) {
                    data.textureSetRevisionSpecific.textureEdits = undefined
                    await this.$_operatorStack()?.setTextureEdits(null)
                }
                this.$data.set(this.$data())
                await this.saveAndStartProcessing()
                void this.setTextureSetRevision(newTextureSetRevisionId, {warnIfEdited: false, forceReload: true})
            } finally {
                operatorStack.muteUpdates = false // re-enable operator stack updates
            }
        } finally {
            this.busyDec()
        }
    }

    protected async importOperatorsFromRevision() {
        const operatorStack = this.$_operatorStack()
        if (!operatorStack) {
            throw Error("No operator stack set.")
        }

        // Step 1: Ask for texture set legacy ID
        const idDialogRef = this.dialog.open(RenameDialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                currentName: "",
                caption: "Enter texture set legacy ID",
                confirmLabel: "Load",
            },
        })
        const textureSetLegacyId = await firstValueFrom(idDialogRef.afterClosed())
        if (!textureSetLegacyId || typeof textureSetLegacyId !== "string") {
            return
        }
        const parsedLegacyId = parseInt(textureSetLegacyId.trim(), 10)
        if (isNaN(parsedLegacyId)) {
            this.notification.showError("Invalid legacy ID. Please enter a numeric ID.")
            return
        }

        // Step 2: Fetch texture set and its revisions (show busy indicator during network requests)
        let selectedEditsJson: TextureEditNodes.TextureEdits
        this.busyInc()
        try {
            const textureSetData = await this.textureApi.queryTextureSetByLegacyId(parsedLegacyId)
            const revisionDescriptors = textureSetData.textureSetRevisions

            // Step 3: Sort revisions like the editor tabs and fetch each revision's editsJson
            const sortedDescriptors = [...revisionDescriptors]
                .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
                .map((revision, index) => ({...revision, version: index + 1}))
                .reverse()

            const revisionsWithEdits: {version: number; name: string | null | undefined; editsJson: TextureEditNodes.TextureEdits}[] = []
            for (const descriptor of sortedDescriptors) {
                const revisionData = await this.textureApi.queryTextureSetRevision(descriptor.id)
                if (
                    revisionData.editsJson &&
                    revisionData.editsJson.type === "texture-edits" &&
                    revisionData.editsJson.schema >= MIN_SUPPORTED_SCHEMA_VERSION
                ) {
                    revisionsWithEdits.push({
                        version: descriptor.version,
                        name: descriptor.name,
                        editsJson: revisionData.editsJson as TextureEditNodes.TextureEdits,
                    })
                }
            }

            if (revisionsWithEdits.length === 0) {
                this.notification.showError("No revisions with valid editing steps found in this texture set.")
                return
            }

            // Step 4: Pick a revision
            if (revisionsWithEdits.length === 1) {
                selectedEditsJson = revisionsWithEdits[0].editsJson
            } else {
                const otherActions = revisionsWithEdits.map((rev, index) => ({
                    state: String(index),
                    label: getRevisionTitle(rev, rev.version),
                }))
                const pickDialogRef = this.dialog.open(DialogComponent, {
                    disableClose: false,
                    width: "400px",
                    data: {
                        title: "Select revision",
                        message: "Select a revision to copy operators from:",
                        confirmLabel: "",
                        cancelLabel: "Cancel",
                        otherActions,
                    },
                })
                const picked = await firstValueFrom(pickDialogRef.afterClosed())
                if (picked === false || picked == null) {
                    return
                }
                selectedEditsJson = revisionsWithEdits[Number(picked)].editsJson
            }
        } catch (error) {
            this.notification.showError("Error importing operators: " + error)
            return
        } finally {
            this.busyDec()
        }

        // Step 5: Load operators by emitting through sourceDataChanged (same path as normal load)
        const data = this.$data()
        if (!data?.textureTypeSpecific) {
            this.notification.showError("No texture data available.")
            return
        }
        data.textureSetRevisionSpecific.textureEdits = selectedEditsJson
        const dataObject =
            data.textureSetRevisionSpecific.autoGenerateDisplacementMap && data.textureTypeSpecific.textureType === TextureType.Displacement
                ? data.textureTypeSpecific.resultDataObject
                : data.textureTypeSpecific.sourceDataObject
        // Mark operators as edited once the result image has been rendered
        const subscription = operatorStack.resultImageChanged.pipe(filter(IsDefined), first()).subscribe(() => {
            for (const operator of operatorStack.operators) {
                if (!operator.flags.has("is-internal")) {
                    operator.markEdited()
                }
            }
        })
        subscription.add(() => subscription.unsubscribe())
        this.sourceDataChanged.emit({
            sourceImage: {
                dataObjectId: dataObject.id,
                isResult: data.textureSetRevisionSpecific.autoGenerateDisplacementMap && data.textureTypeSpecific.textureType === TextureType.Displacement,
                downloadFormat: TextureEditorSettings.UseVT ? "tiles" : "jpg",
            },
            textureEdits: selectedEditsJson,
        })
        this.notification.showInfo("Operators imported successfully.")
    }

    protected async deleteCurrentRevision() {
        const data = this.$data()
        if (!data) {
            throw Error("No texture set revision data set.")
        }
        this.busyInc()
        try {
            const textureSetRevisionToDelete = data.textureSetRevisionSpecific.textureSetRevision
            const referencedByMaterials = textureSetRevisionToDelete.materialRevisionReferences
                .map(
                    (reference) =>
                        `${reference.materialRevision.material.name} (${reference.materialRevision.material.legacyId}) v${reference.materialRevision.number}`,
                )
                .filter(IsUnique)
            if (referencedByMaterials.length > 0) {
                this.notification.showError(
                    "The selected revision is referenced by a material node and can not be deleted. Please delete the referencing material node first.\n\nThe referencing material revisions are:\n" +
                        referencedByMaterials.join(", "),
                    10000,
                )

                return
            }
            const isLastRevision = data.textureSetRevisionSpecific.textureSet.textureSetRevisions.length === 1
            await this.notification
                .confirmationDialog({
                    title: "Delete current revision",
                    message: isLastRevision
                        ? `This is the last revision. Deleting it will also <strong>permanently remove all map data</strong>. This action cannot be undone. Are you sure?`
                        : `Are you sure you want to delete the current revision?`,
                    confirm: "Delete",
                    cancel: "Abort",
                    isDestructive: true,
                })
                .then(async (confirmed) => {
                    if (confirmed) {
                        if (this.$isProcessingEdits()) {
                            // in case we're still processing edits, cancel those
                            await this._textureEditProcessing.cancelBakingJob(textureSetRevisionToDelete)
                        }
                        await this.notification.withUserFeedback(() => this.textureApi.deleteTextureSetRevision(textureSetRevisionToDelete.id), {
                            success: () => {
                                const currentRevisionIndex = data.textureSetRevisionSpecific.textureSet.textureSetRevisions.findIndex(
                                    (revision) => revision.id === textureSetRevisionToDelete.id,
                                )
                                if (currentRevisionIndex < 0) {
                                    throw Error("INTERNAL ERROR: No texture set revision found.")
                                }
                                let nextRevisionIndex: number | undefined
                                if (currentRevisionIndex < data.textureSetRevisionSpecific.textureSet.textureSetRevisions.length - 1) {
                                    nextRevisionIndex = currentRevisionIndex + 1
                                } else if (currentRevisionIndex > 0) {
                                    nextRevisionIndex = currentRevisionIndex - 1
                                }
                                if (nextRevisionIndex != null) {
                                    this.setTextureSetRevision(data.textureSetRevisionSpecific.textureSet.textureSetRevisions[nextRevisionIndex].id, {
                                        warnIfEdited: false,
                                    })
                                } else {
                                    this.$overlayDialog().closeIfClean()
                                }
                                return "Revision deleted."
                            },
                            error: "Error deleting texture edit.",
                        })
                    }
                })
        } finally {
            this.busyDec()
        }
    }

    protected async save() {
        if (!this.$_operatorStack()) {
            throw Error("No operator stack set.")
        }
        if (!this.$data()) {
            throw Error("No texture set revision data set.")
        }
        this.busyInc()
        try {
            await this.notification.withUserFeedback(
                async () => {
                    await this.saveAndStartProcessing()
                },
                {
                    success: () => "Saved successfully.",
                    error: (msg) => "Saving failed: " + msg,
                },
            )
        } finally {
            this.busyDec()
        }
    }

    protected async discardChanges() {
        const data = this.$data()
        if (!data) {
            throw Error("No texture set revision data set.")
        }
        const operatorStack = this.$_operatorStack()
        if (!operatorStack) {
            throw Error("No operator stack set.")
        }
        this.notification
            .confirmationDialog({
                title: "Discard changes",
                message: `Are you sure you want to discard the current changes ?`,
                confirm: "Discard",
                cancel: "Abort",
                isDestructive: true,
            })
            .then(async (confirmed) => {
                if (confirmed) {
                    await operatorStack.setTextureEdits(data.textureSetRevisionSpecific.textureEdits ?? null)
                }
            })
    }

    private async saveAndStartProcessing() {
        const data = this.$data()
        if (!data) {
            throw Error("No texture set revision data set.")
        }
        const operatorStack = this.$_operatorStack()
        if (!operatorStack) {
            throw Error("No operator stack set.")
        }
        const textureSetRevision = data.textureSetRevisionSpecific.textureSetRevision
        const pxPerCm = data.textureTypeSpecific?.pxPerCm.width
        if (!pxPerCm) {
            throw Error("No pixel per cm set.")
        }
        operatorStack.setBusy(true)
        try {
            await operatorStack.refreshFullStackInHalMode()
        } finally {
            operatorStack.setBusy(false)
        }
        const processingJobId = await this._textureEditProcessing.createBakingJob(operatorStack, textureSetRevision, pxPerCm)
        const textureEdits = await this.collectTextureEdits(operatorStack, textureSetRevision, processingJobId)
        let mapAssignments: TextureSetRevisionMapAssignmentInput[] | undefined = undefined
        if (processingJobId) {
            // if we spawn a new processing job then we attach the original source maps until it is done and remove the old baked maps if they are unused
            const sourceMapDataObjectIds = getSourceMapDataObjectIds(textureSetRevision)
            mapAssignments = Array.from(sourceMapDataObjectIds.entries()).map(([textureType, dataObjectId]) => ({
                textureType,
                dataObjectId,
            }))
        }
        await this.textureApi.updateTextureSetRevision(data.textureSetRevisionSpecific.textureSet.id, {
            id: textureSetRevision.id,
            editsProcessingJobId: processingJobId,
            editsJson: textureEdits,
            mapAssignments,
            autoRescaleSize: true,
        })
        // update in memory data
        data.textureSetRevisionSpecific.textureSetRevision.updatedAt = new Date().toISOString()
        data.textureSetRevisionSpecific.textureSetRevision.updatedBy = await this.authService.user
        data.textureSetRevisionSpecific.textureSetRevision.editsJson = textureEdits
        data.textureSetRevisionSpecific.textureEdits = textureEdits
        if (processingJobId) {
            data.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob.set({id: processingJobId, state: JobState.Init, __typename: "Job"})
            this.refresh.item({id: data.textureSetRevisionSpecific.textureSet.id, __typename: "TextureSet"})
        }
        this.$data.set(this.$data())
        operatorStack.resetChangedFlag()
        await this.checkForProcessingEdits()
    }

    // backfills sourceStatsCache/sourceMeanCache into editsJson for edits that predate that caching, so the next load is fast
    private async maybePersistCacheBackfill(): Promise<void> {
        function roundFloatsToComparisonPrecision(value: unknown): unknown {
            const comparisonPrecision = 9 // significant digits; above GPU/curve-fit float noise, below any real difference
            if (typeof value === "number") {
                return Number.isFinite(value) ? Number(value.toPrecision(comparisonPrecision)) : value
            }
            if (Array.isArray(value)) {
                return value.map(roundFloatsToComparisonPrecision)
            }
            if (value && typeof value === "object") {
                return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, roundFloatsToComparisonPrecision(entry)]))
            }
            return value
        }

        // operator save() can re-derive fields from live state (e.g. OperatorTiling's curve-fit normalizedCurvePosition) or
        // GPU-reduction stats (recolor/highpass sourceStatsCache) that are never bit-exact across a load->save round-trip
        function textureEditsAreEquivalent(a: TextureEditNodes.TextureEdits, b: TextureEditNodes.TextureEdits | null): boolean {
            return deepEqual(roundFloatsToComparisonPrecision(a), roundFloatsToComparisonPrecision(b))
        }

        const operatorStack = this.$_operatorStack()
        const data = this.$data()
        if (!operatorStack || !data) {
            return
        }
        if (!this.$isDataValid()) {
            return
        }
        try {
            const textureSetRevision = data.textureSetRevisionSpecific.textureSetRevision
            if (operatorStack.isEdited) {
                this.logger.warn("isEdited is set even so we just loaded initially. This should not happen")
                return
            }
            const freshTextureEdits = await this.collectTextureEdits(operatorStack, textureSetRevision, undefined)
            const currentTextureEdits: TextureEditNodes.TextureEdits = JSON.parse(JSON.stringify(freshTextureEdits)) // drop explicit-undefined keys a real JSON payload never has
            const persistedTextureEdits = data.textureSetRevisionSpecific.textureEdits ?? null
            if (textureEditsAreEquivalent(currentTextureEdits, persistedTextureEdits)) {
                return
            }
            this.logger.log("Updating texture edits json due to divergence.")
            await this.textureApi.updateTextureSetRevision(data.textureSetRevisionSpecific.textureSet.id, {
                id: textureSetRevision.id,
                editsJson: currentTextureEdits,
                updatedAt: textureSetRevision.updatedAt ?? textureSetRevision.createdAt,
                updatedBy: textureSetRevision.updatedBy?.id ?? textureSetRevision.createdBy?.id,
            })
            data.textureSetRevisionSpecific.textureSetRevision.editsJson = currentTextureEdits
            data.textureSetRevisionSpecific.textureEdits = currentTextureEdits
        } catch (e) {
            this.logger.warn("Failed to persist texture edit cache backfill", e)
        }
    }

    private async collectTextureEdits(
        operatorStack: OperatorStack,
        textureSetRevision: TextureSetRevisionDataFragment,
        processingJobId: string | undefined,
    ): Promise<TextureEditNodes.TextureEdits> {
        const operatorNodes = (await Promise.all(operatorStack.operators.map((operator) => operator.save(processingJobId)))).filter(IsDefined)
        return this.makeTextureEditsFromOperatorNodes(textureSetRevision, operatorNodes)
    }

    private makeTextureEditsFromOperatorNodes(
        textureSetRevision: TextureSetRevisionDataFragment,
        operatorNodes: TextureEditNodes.Operator[],
    ): TextureEditNodes.TextureEdits {
        const textureRevisionId = getSourceMapDataObjectIds(textureSetRevision)
        const dataObjectByTextureType: TextureEditNodes.TextureSet["dataObjectByTextureType"] = {}
        textureRevisionId.forEach((id, textureType) => {
            dataObjectByTextureType[textureType] = {
                type: "data-object-reference",
                dataObjectId: id,
            }
        })
        return {
            type: "texture-edits",
            schema: TextureEditNodes.CURRENT_SCHEMA,
            maps: {
                type: "texture-set",
                dataObjectByTextureType,
            },
            operators: operatorNodes,
        }
    }

    // protected downloadGraph(): void {
    //     this.$_operatorStack()?.downloadOperatorNodeGraph()
    // }

    readonly $hasOperatorStack = computed(() => this.$_operatorStack() != null)

    protected readonly $operatorStack = computed(() => {
        const stack = this.$_operatorStack()
        if (!stack) {
            throw Error("No operator stack set.")
        }
        return stack
    })

    readonly $selectedOperator = computed(() => this.$_operatorStack()?.selectedOperator ?? null)

    setTextureSetRevision(textureSetRevisionId: string, options?: {warnIfEdited?: boolean; forceReload?: boolean}) {
        const warnIfEdited = options?.warnIfEdited ?? true
        const forceReload = options?.forceReload ?? false
        if (!forceReload && this.$data()?.textureSetRevisionSpecific.textureSetRevision.id === textureSetRevisionId) {
            return
        }
        const navToRevision = (textureSetRevisionId: string) => {
            return this.navigateToTextureSetRevision(textureSetRevisionId, this.$data()?.textureTypeSpecific?.textureType)
        }
        if (!this.$isEdited() || !warnIfEdited) {
            void navToRevision(textureSetRevisionId)
        } else {
            const dialogRef = this.dialog.open(DialogComponent, {
                disableClose: false,
                width: "400px",
                data: {
                    title: "Switch texture revision",
                    message:
                        "You are attempting to switch the texture revision which will cause your <strong>unsaved changes to be lost</strong>.<br><br>Are you sure you want to continue ?",
                    confirmLabel: "Switch revision and lose changes",
                    cancelLabel: "Cancel",
                },
            })
            dialogRef
                .afterClosed()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((confirmed) => {
                    if (confirmed) {
                        navToRevision(textureSetRevisionId)
                    }
                })
        }
    }

    setTextureType(textureType: TextureType) {
        const data = this.$data()
        if (!data) {
            throw Error("No selected texture revision info.")
        }
        this.navigateToTextureSetRevision(data.textureSetRevisionSpecific.textureSetRevision.id, textureType)
    }

    private async navigateToTextureSetRevision(textureSetRevisionId: string, textureType: TextureType | undefined): Promise<void> {
        return this.router
            .navigate(["../", textureSetRevisionId], {
                relativeTo: this.route,
                queryParamsHandling: "merge",
                queryParams: {textureType},
            })
            .then((result) => {
                // In case of switching between a newly tiled texture and another texture version, the route URL does not change so this has to be triggered manually.
                if (result == null) {
                    this.changeTexture()
                }
            })
    }

    // TODO migrate to GQL api
    // private setupMaterialGraphFromTextureSet(textureSet: TextureSet): Observable<MaterialGraph> {
    //     const set0 = new Set(MATERIAL_TEMPLATE_0_TEXTURE_TYPES)
    //     const set1 = new Set(MATERIAL_TEMPLATE_1_TEXTURE_TYPES)
    //     let dimensionRange: {maxWidth: number; minWidth: number; maxHeight: number; minHeight: number}
    //     const typeToRevision = new Map<string, number>()
    //     // TODO: This can be removed at some point. Only required because there was a rounding error in the displacement map calculation.
    //     const checkTextures = new Set([...MATERIAL_TEMPLATE_0_TEXTURE_TYPES, ...MATERIAL_TEMPLATE_1_TEXTURE_TYPES])
    //     textureSet.textures.map((texture) => {
    //         if (!checkTextures.has(texture.type)) return
    //         if (set0.has(texture.type)) set0.delete(texture.type)
    //         if (set1.has(texture.type)) set1.delete(texture.type)

    //         const revision = texture.getLatestRevision()
    //         if (!dimensionRange) {
    //             dimensionRange = {
    //                 maxWidth: revision.width,
    //                 minWidth: revision.width,
    //                 maxHeight: revision.height,
    //                 minHeight: revision.height,
    //             }
    //         } else {
    //             dimensionRange.maxWidth = Math.max(dimensionRange.maxWidth, revision.width)
    //             dimensionRange.minWidth = Math.min(dimensionRange.minWidth, revision.width)
    //             dimensionRange.maxHeight = Math.max(dimensionRange.maxHeight, revision.height)
    //             dimensionRange.minHeight = Math.min(dimensionRange.minHeight, revision.height)
    //         }

    //         typeToRevision.set(texture.type.label, revision.id)
    //     })
    //     if (
    //         Math.abs(dimensionRange.maxWidth - dimensionRange.minWidth) > ALLOWED_MAP_DIMENSION_VARIATION_IN_CM ||
    //         Math.abs(dimensionRange.maxHeight - dimensionRange.minHeight) > ALLOWED_MAP_DIMENSION_VARIATION_IN_CM
    //     ) {
    //         throw new Error("Non matching physical sizes for texture revisions")
    //     }
    //     if (set0.size !== 0 && set1.size !== 0) throw new Error("Not all required texture types present")

    //     const templateRevisionId = set0.size === 0 ? Settings.MATERIAL_TEMPLATE_0_MATERIAL_REVISION_ID : Settings.MATERIAL_TEMPLATE_1_MATERIAL_REVISION_ID
    //     let queryParams: HttpParams = new HttpParams()
    //     queryParams = queryParams.set("material_revision", String(templateRevisionId))

    //     const dimensions = [(dimensionRange.maxWidth + dimensionRange.minWidth) * 0.5, (dimensionRange.maxHeight + dimensionRange.minHeight) * 0.5]
    //     const nodesOp = MaterialNode.getAll(queryParams).pipe(
    //         switchMap((templateMaterialNodes) =>
    //             forkJoinZeroOrMore([observableOf(templateMaterialNodes), forkJoin(this._textureRevisionToType(templateMaterialNodes))])
    //         ),
    //         map(([materialNodes, templateRevisionToType]) => {
    //             materialNodes = materialNodes.map((node) => {
    //                 if (node.name === "Mapping") {
    //                     const idx = node.parameters.findIndex((p) => p.name === "Scale")
    //                     node.parameters[idx].value = [...dimensions, 1.0]
    //                 } else if (node.name === "TexImage") {
    //                     node.textureRevision = typeToRevision.get(templateRevisionToType[node.textureRevision])
    //                 }
    //                 return node
    //             })
    //             this.nodes = materialNodes
    //         })
    //     )

    //     const connectionsOp = MaterialConnection.getAll(queryParams).pipe(
    //         map((materialConnections) => {
    //             this.connections = materialConnections
    //         })
    //     )

    //     return forkJoinZeroOrMore([nodesOp, connectionsOp]).pipe(
    //         switchMap(() =>
    //             MaterialGraph.fromNodesAndConnections(this.nodes, this.connections, activeRevisionId, this.material.id, this.material.name).pipe(
    //                 map((graph) => {
    //                     this.materialGraph = graph
    //                     if (updateNodesAndConnections) {
    //                         this.nodeEditor.updateNodesAndConnections(this.nodes)
    //                     }
    //                 })
    //             )
    //         )
    //     )
    // }

    setBusy(busy: boolean) {
        if (busy) {
            this.busyInc()
        } else {
            this.busyDec()
        }
    }

    private busyInc() {
        this.$_busyCounter.update((value) => value + 1)
    }

    private busyDec() {
        this.$_busyCounter.update((value) => value - 1)
    }

    readonly $data = signal<TextureEditorData | undefined>(undefined)
    readonly $resultResolution = signal(new Size2(0, 0), {equal: (a, b) => a.width === b.width && a.height === b.height})

    protected TextureType = TextureType
    readonly $getRevisionTitle = signal(getRevisionTitle)

    private readonly $_busyCounter = signal(0)
    private _lastRequestedTextureInfo: {textureSetRevisionId: string; textureType?: TextureType} = {textureSetRevisionId: ""}
    private readonly $_operatorStack = signal<OperatorStack | undefined>(undefined)
    private _textureEditProcessing: TextureEditProcessing
    private readonly $_isEdited = signal(false)
    private readonly $_loadingComplete = signal(false)
    private readonly $_canvasLoadError = signal<Error | null>(null)
    private _tilingCanvasShowsResultDataObject = false
    private _handledFirstFrame = false
    private _cacheBackfillAttemptedForRevision = false

    navigateBack() {
        void this.$overlayDialog().closeIfClean()
    }
}

// detects editsJson referencing an operator type we no longer support (e.g. a removed legacy operator), which can
// no longer be reconstructed into an operator stack - a pure JSON scan, deliberately not going through operator
// deserialization, so it stays safe to call even on data the operator stack itself can't handle anymore
export const editsJsonHasUnsupportedOperator = (editsJson: unknown): boolean => {
    if (!editsJson || typeof editsJson !== "object") {
        return false
    }
    const operators = (editsJson as {operators?: unknown}).operators
    return (
        Array.isArray(operators) &&
        operators.some((op) => {
            const type = op && typeof op === "object" ? (op as {type?: unknown}).type : undefined
            return typeof type !== "string" || !isKnownOperatorType(type)
        })
    )
}
